import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  MapPin,
  Wine,
  Layers,
  Grid3x3,
  FileText,
  Award,
  Ruler,
  Leaf,
  Sparkles,
  Boxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison : 3 engagements fermes ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos fournitures à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pas besoin de perdre votre samedi à courir les magasins ou à
            tenter de faire entrer des piles de cartons encombrantes dans
            votre véhicule. Nous déployons notre propre logistique pour
            déposer votre commande sur votre pas de porte à {city.name}.
          </p>
          <div className="mt-4 text-[12px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
            Nos 3 engagements fermes
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Maîtrise totale de la chaîne",
              body: "Nous éliminons les risques liés aux transporteurs externes ou aux réseaux de messagerie généraux. Nos propres collaborateurs manipulent et chargent vos produits, assurant un respect rigoureux de chaque colis.",
              Icon: Truck,
            },
            {
              title: "Hygiène et préservation mécanique",
              body: "Vos emballages sont stockés et acheminés dans des véhicules hermétiques aux intempéries. Vous avez la garantie de recevoir un matériel exempt de poussière, parfaitement sec et n'ayant subi aucune déformation structurelle.",
              Icon: ShieldCheck,
            },
            {
              title: "Optimisation des flux locaux",
              body: "En restreignant notre rayon d'action à la seule région francilienne, nous garantissons une réactivité de pointe et une flexibilité horaire maximale, calée sur le rythme de vos préparatifs.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Barrières antichocs : split avec visuel fragile ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Wine className="h-3.5 w-3.5" strokeWidth={2.6} />
              Fragiles sécurisés
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Protégez vos objets fragiles grâce à nos barrières antichocs
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Assiettes, verres, miroirs ou appareils électroniques&nbsp;:
              les pièces délicates demandent une attention chirurgicale.
              Pour éliminer les risques de bris durant les vibrations du
              camion, nous avons développé des solutions d&apos;emballage
              ciblées.
            </p>

            {/* Big visual stat card */}
            <div className="mt-7 relative overflow-hidden bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] text-white rounded-2xl p-6 md:p-7">
              <div
                aria-hidden="true"
                className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#FF7A00]/30 blur-2xl"
              />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
                  Le constat
                </div>
                <div className="mt-2 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-white">
                  1 verre sur 4
                </div>
                <p className="mt-3 text-[14px] text-white/75 leading-relaxed">
                  C&apos;est le taux de casse moyen observé lorsque la
                  vaisselle est emballée dans un carton standard sans
                  alvéoles ni film bulle. Avec nos cartons techniques, ce
                  chiffre tombe à quasi zéro.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-3">
            {[
              {
                title: "Le film bulle haute densité",
                body: "Une véritable armure d'air qui enveloppe vos objets d'art, bibelots et écrans pour neutraliser les impacts extérieurs.",
                Icon: Layers,
                cta: {
                  to: "/produit/papier-bulle-10m",
                  label: "Voir le papier bulle 10 m",
                  testId: "city-fontenay-cta-bulle",
                },
              },
              {
                title: "La technologie des alvéoles protectrices",
                body: "Fini le calage artisanal avec du papier journal qui tache les mains et prend un temps infini. Nos boîtes techniques intègrent des séparateurs rigides en carton double cannelure. Chaque assiette et chaque verre trouve sa place instantanément dans un logement sur mesure.",
                Icon: Grid3x3,
                cta: {
                  to: "/produit/carton-vaisselle",
                  label: "Voir le carton à vaisselle alvéolé",
                  testId: "city-fontenay-cta-vaisselle",
                },
              },
              {
                title: "Le papier de soie et calage",
                body: "Idéal pour combler les espaces vides à l'intérieur de vos cartons et bloquer toute velléité de mouvement de vos affaires pendant le transit.",
                Icon: FileText,
                cta: {
                  to: "/produit/papier-blanc-journal",
                  label: "Voir le papier de calage",
                  testId: "city-fontenay-cta-papier",
                },
              },
            ].map(({ title, body, Icon, cta }) => (
              <div
                key={title}
                className="flex items-start gap-3 bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div className="flex-1">
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                  <Link
                    to={cta.to}
                    className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all"
                    data-testid={cta.testId}
                  >
                    {cta.label}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi retenir notre matériel : 3 fiches techniques ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Critères techniques
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi retenir notre matériel&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire le choix de nos fournitures, c&apos;est investir dans la
            tranquillité d&apos;esprit grâce à des critères techniques
            stricts.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              stat: "DC",
              statSub: "Double cannelure",
              title: "Un grammage supérieur",
              body: "La composition de nos cartons offre une résistance mécanique très élevée, permettant de les empiler sur plusieurs niveaux dans le camion sans qu'ils ne s'écrasent.",
              Icon: Award,
              accent: "#237cb5",
            },
            {
              stat: "≤20",
              statSub: "kg / carton",
              title: "Des formats pensés pour le corps humain",
              body: "Nos tailles de boîtes (standards, livres, penderies) sont calibrées pour optimiser la répartition des charges, vous évitant de surcharger les volumes et de vous blesser le dos.",
              Icon: Ruler,
              accent: "#FF7A00",
            },
            {
              stat: "100%",
              statSub: "Recyclable",
              title: "Une démarche responsable",
              body: "Nos emballages intègrent des fibres biodégradables et entièrement recyclables, conciliant efficacité logistique et respect de l'environnement.",
              Icon: Leaf,
              accent: "#237cb5",
            },
          ].map(({ stat, statSub, title, body, Icon, accent }) => (
            <article
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
            >
              {/* Top stat strip */}
              <div
                className="px-5 py-5 flex items-end justify-between"
                style={{
                  background:
                    "linear-gradient(135deg, #f7f9fc 0%, #ffffff 100%)",
                  borderBottom: `3px solid ${accent}`,
                }}
              >
                <div>
                  <div
                    className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-none"
                    style={{ color: accent }}
                  >
                    {stat}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.16em] font-bold text-[#3a4856]">
                    {statSub}
                  </div>
                </div>
                <span
                  className="flex items-center justify-center h-11 w-11 rounded-full text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
              </div>
              {/* Body */}
              <div className="p-5 md:p-6 flex-1">
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-fontenay-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-fontenay-cta-packs"
          >
            Voir les kits complets
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-fontenay-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
